import { useCallback, useMemo, useState } from 'react';
import type { PricedItem } from '@/app/types/item';
import { lineTotals, resolveRate, type LineTotals } from '@/app/lib/pricing';

export interface CartLine {
    item: PricedItem;
    quantity: number;
    rate: number;
    /** What a standard customer would pay, from the quote; sent with the line. */
    standardRate: number | null;
    tierLabel: string | null;
    totals: LineTotals;
}

/**
 * Order cart state.
 *
 * Holds the full PricedItem alongside the quantity so the review sheet can still
 * render and price a line after the manager has browsed away to another category —
 * product pages are fetched live and are not retained.
 *
 * Rates are re-resolved on every render because they are quantity-dependent:
 * crossing a tier boundary changes the rate for the whole line.
 */
export function useOrderCart() {
    const [entries, setEntries] = useState<Map<number, { item: PricedItem; quantity: number }>>(
        () => new Map()
    );

    const setQuantity = useCallback((item: PricedItem, quantity: number) => {
        setEntries((prev) => {
            const next = new Map(prev);
            if (quantity <= 0) {
                next.delete(item.id);
            } else {
                next.set(item.id, { item, quantity });
            }
            return next;
        });
    }, []);

    const getQuantity = useCallback(
        (itemId: number) => entries.get(itemId)?.quantity ?? 0,
        [entries]
    );

    const clear = useCallback(() => setEntries(new Map()), []);

    const lines = useMemo<CartLine[]>(() => {
        return Array.from(entries.values()).map(({ item, quantity }) => {
            const resolved = resolveRate(item, quantity);
            return {
                item,
                quantity,
                rate: resolved.rate,
                standardRate: resolved.standardRate,
                tierLabel: resolved.tierLabel,
                totals: lineTotals(
                    quantity,
                    resolved.rate,
                    item.taxPercent,
                    item.isTaxInclusive
                ),
            };
        });
    }, [entries]);

    const grandTotal = useMemo(
        () => lines.reduce((sum, line) => sum + line.totals.totalAmount, 0),
        [lines]
    );

    return {
        lines,
        itemCount: lines.length,
        grandTotal,
        setQuantity,
        getQuantity,
        clear,
    };
}
